<template>
  <!-- Membership Selection Modal -->
  <GetProductMembershipModal
    v-if="subscriptionProducts"
    v-model="showUpgradeModal"
    :requested-permission-information="requestedPermissionInformation"
    :products="subscriptionProducts"
    :loading="isLoadingProducts"
    @selected="onMembershipSelection"
    @close="onModalClosed"
  />

  <!-- Membership Checkout Modal -->
  <MembershipCheckoutModal
    v-if="selectedProductId"
    v-model="showCheckoutModal"
    :product-id="selectedProductId"
    @close="onCheckoutClosed"
  />
</template>

<script setup lang="ts">
import { ref, computed, defineAsyncComponent } from "vue";
import { useChannelStore } from "@/stores/channel.store";
import { useCapabilitiesStore } from "@/stores/capabilities.store";
import { useSubscriptionProducts } from "@/services/purchase/purchase.service";
import { useGatingEvaluator } from "@/features/gating/evaluation/use-gating-evaluator";
import { ConversionType } from "@/generated/graphql";
import {
  ChannelEventType,
  useChannelEvents,
} from "@/composables/channel-events";
import type { AuthorizationRuleForGating } from "@/features/gating/use-gating-permissions";

// Lazy load modals
const GetProductMembershipModal = defineAsyncComponent(
  () => import("./membership/get-product-membership-modal.vue"),
);
const MembershipCheckoutModal = defineAsyncComponent(
  () => import("./membership/membership-checkout-modal.vue"),
);

const emit = defineEmits<{
  "membership-purchased": [];
  "upgrade-cancelled": [];
}>();

const _capabilitiesStore = useCapabilitiesStore();
const _channelStore = useChannelStore();
const { getGatingMaskInformation } = useGatingEvaluator();
const { emitChannelEvent } = useChannelEvents();

// Internal state
const showUpgradeModal = ref(false);
const showCheckoutModal = ref(false);
const selectedProductId = ref<string | null>(null);
const requestedRule = ref<AuthorizationRuleForGating | null>(null);

// Fetch subscription products using composable (query runs when modal is open)
const { subscriptionProducts, loading: isLoadingProducts } =
  useSubscriptionProducts({
    enabled: showUpgradeModal,
  });

// Computed permission information for modal
const requestedPermissionInformation = computed(() => {
  const permissionInfo = getGatingMaskInformation(requestedRule.value);
  if (!permissionInfo || !requestedRule.value) return null;

  return {
    ...permissionInfo,
    significant_permissions_mask:
      requestedRule.value.significant_permissions_mask,
  };
});

// Public methods (exposed to parent via template ref)
function showUpgradeFlow(rule?: AuthorizationRuleForGating | null) {
  requestedRule.value = rule ?? null;
  showUpgradeModal.value = true;
}

function showCheckoutFlow(productId: string) {
  selectedProductId.value = productId;
  showCheckoutModal.value = true;
}

// Event handlers
function onMembershipSelection(productId: string, isFree: boolean) {
  showUpgradeModal.value = false;

  if (isFree) {
    emit("upgrade-cancelled");
    emitChannelEvent({
      type: ChannelEventType.UPGRADE_FINISHED,
      conversionType: ConversionType.Free,
    });
  } else {
    selectedProductId.value = productId;
    showCheckoutModal.value = true;
  }
}

function onModalClosed() {
  showUpgradeModal.value = false;
  requestedRule.value = null;
  emit("upgrade-cancelled");
}

function onCheckoutClosed() {
  showCheckoutModal.value = false;
  selectedProductId.value = null;
  emit("upgrade-cancelled");
}

// Expose methods to parent
defineExpose({
  showUpgradeFlow,
  showCheckoutFlow,
});
</script>
